import React, { useState } from 'react';
import {
  Coins,
  CreditCard,
  CheckCircle2,
  RotateCcw,
  Wallet,
  Sparkles,
  Clock,
  Layers,
  Ban,
  ShieldAlert,
  AlertTriangle,
  FileText,
  Mail,
  ChevronRight,
  ShieldCheck
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'overview', title: '1. Understanding Credits', icon: Coins },
  { id: 'what-is-credit', title: '2. What Is a Credit?', icon: CreditCard },
  { id: 'credit-deduction', title: '3. Credit Deduction Rules', icon: CheckCircle2 },
  { id: 'failed-actions', title: '4. Failed Actions & Reversals', icon: RotateCcw },
  { id: 'credit-balance', title: '5. Credit Balance & Bonus', icon: Wallet },
  { id: 'expiration', title: '6. Credit Expiration & Validity', icon: Clock },
  { id: 'subscription-tiers', title: '7. Subscription Tiers', icon: Layers },
  { id: 'unused-refunds', title: '8. Unused Credits & Refunds', icon: Ban },
  { id: 'suspension-misuse', title: '9. Suspension & Misuse', icon: ShieldAlert },
  { id: 'policy-changes', title: '10. Policy Changes', icon: FileText },
  { id: 'need-help', title: '11. Support & Contacts', icon: Mail },
];

const keyPrinciples = [
  'Credit costs are explicitly displayed before any action is confirmed.',
  'Credits are deducted strictly upon successful completion of the action.',
  'Technical issues or system failures qualify for credit restoration review.',
  'Bonus/promotional credits may carry separate validity rules and zero cash value.',
  'Unused credits are generally non-refundable and tied to active subscription cycles.',
  'Subscription tiers (Ignite, Accelerate, Dominate) govern monthly credit allocations.',
];

const subscriptionPlans = [
  {
    name: 'Ignite',
    badge: 'Starter Tier',
    desc: 'Designed for targeted executive searches with essential monthly credit allocations and core platform access.',
  },
  {
    name: 'Accelerate',
    badge: 'Growth Tier',
    desc: 'Expanded credit capacity for active recruiting teams needing regular candidate invitations and contact unlocks.',
  },
  {
    name: 'Dominate',
    badge: 'Enterprise Tier',
    desc: 'High-volume credit allocation with priority support, dedicated account management, and maximum reach.',
  },
];

export const CreditUsagePolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                BILLING & CREDITS
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Credit Usage Policy
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <Coins className="w-4 h-4 text-cyan-400" />
            <span>Transparent Credit Economy</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Policy Navigation
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1: Overview */}
          <section id="overview" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Coins className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Understanding Nexgent Credits</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              Nexgent uses a transparent credit-based framework to provide recruiters with access to executive recruitment features and direct candidate engagement tools.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Credits are allocated through eligible subscription plans and may be consumed to perform specific actions across the platform. This policy outlines how credits are earned, deducted, managed, and expired.
            </p>
          </section>

          {/* Section 2: What Is a Credit? */}
          <section id="what-is-credit" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CreditCard className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. What Is a Credit?</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              A credit is a internal platform unit that allows verified recruiters to execute premium actions within Nexgent.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold font-mono uppercase">Key Credit Properties:</div>
              <ul className="text-slate-400 space-y-1.5 list-disc pl-4">
                <li>Included as part of monthly subscription packages or awarded via promotional programs.</li>
                <li>Required credit costs for specific actions are explicitly communicated prior to completion.</li>
                <li>Hold zero cash equivalent value and cannot be transferred outside the organization account.</li>
              </ul>
            </div>
          </section>

          {/* Section 3: Credit Deduction Rules */}
          <section id="credit-deduction" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. When Is a Credit Deducted?</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Credits are deducted strictly when a credit-based action is successfully completed by the platform:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Candidate Invitations</div>
                <div className="text-slate-400">Dispatching an opportunity invitation expressing interest to a target executive.</div>
              </div>
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Contact Detail Unlocks</div>
                <div className="text-slate-400">Accessing direct contact details following candidate acceptance of an invitation.</div>
              </div>
            </div>
            <p className="text-slate-400 text-xs pt-1">
              A credit is deemed consumed once the action is processed and confirmed by the system. Successfully consumed credits are non-reversible unless a technical fault occurs.
            </p>
          </section>

          {/* Section 4: Failed Actions & Credit Reversal */}
          <section id="failed-actions" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <RotateCcw className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Failed Actions and Credit Reversal</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              If an operational or technical issue prevents the completion of a credit-based action, Nexgent will review the transaction record and, where appropriate:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-lg text-slate-300 text-center">
                <span className="text-cyan-400 font-semibold block mb-0.5">Restore Credit</span>
                <span className="text-slate-400 text-[11px]">Return consumed credit to active balance.</span>
              </div>
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-lg text-slate-300 text-center">
                <span className="text-cyan-400 font-semibold block mb-0.5">Reissue Credit</span>
                <span className="text-slate-400 text-[11px]">Grant an equivalent credit unit.</span>
              </div>
              <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-lg text-slate-300 text-center">
                <span className="text-cyan-400 font-semibold block mb-0.5">Account Adjustment</span>
                <span className="text-slate-400 text-[11px]">Apply reasonable balance correction.</span>
              </div>
            </div>
          </section>

          {/* Section 5: Credit Balance & Bonus Credits */}
          <section id="credit-balance" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Wallet className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Credit Balance & Bonus Credits</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Recruiters can monitor available balances in real time via their employer dashboard.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold flex items-center gap-1.5 font-mono uppercase">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Bonus & Promotional Credits</span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Nexgent may grant promotional credits via referral programs or subscription campaigns. Bonus credits carry no cash value, cannot be exchanged for refunds, and may follow specific consumption order and expiration timelines.
              </p>
            </div>
          </section>

          {/* Section 6: Expiration & Validity */}
          <section id="expiration" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Clock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Credit Expiration & Subscription Cycles</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Credits are tied to the active subscription billing cycle during which they were issued.
            </p>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-5">
              <li>Credits remain valid while the associated subscription plan stays active.</li>
              <li>Credits may expire upon subscription plan termination, non-renewal, or cancellation.</li>
              <li>Expiration specifics are shown in the recruiter dashboard billing view.</li>
            </ul>
          </section>

          {/* Section 7: Subscription Tiers */}
          <section id="subscription-tiers" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Layers className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Subscription Validity & Tiers</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Credit allocation volume and usage allowances are governed by the recruiter's active tier:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs pt-1">
              {subscriptionPlans.map((plan, idx) => (
                <div key={idx} className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-white text-sm">{plan.name}</span>
                      <span className="px-2 py-0.5 text-[10px] font-mono bg-cyan-950 text-cyan-400 rounded border border-cyan-800/50">
                        {plan.badge}
                      </span>
                    </div>
                    <p className="text-slate-400 text-[11px] leading-relaxed">{plan.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 8: Unused Credits & Refunds */}
          <section id="unused-refunds" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Ban className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. Unused Credits and Refund Policy</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Unused platform credits cannot be converted to cash, transferred to other accounts, or resold.
            </p>
            <div className="p-3 bg-amber-950/30 border border-amber-900/40 text-amber-300 rounded-lg text-xs">
              Unless required by applicable law or approved in writing under exceptional circumstances, unused credits included in plans or add-on packages are non-refundable.
            </div>
          </section>

          {/* Section 9: Account Suspension & Misuse */}
          <section id="suspension-misuse" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Account Suspension & Credit Misuse</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              The following actions constitute credit misuse and are strictly prohibited:
            </p>
            <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-5">
              <li>Manipulating credit balance registers or exploiting software bugs.</li>
              <li>Unauthorized transfer or resale of credit units.</li>
              <li>Fraudulent billing transactions or circumvention of payment systems.</li>
            </ul>
            <p className="text-xs text-red-400 pt-1">
              Violations may result in credit forfeiture, account restriction, or immediate account termination.
            </p>
          </section>

          {/* Section 10: Changes to Credit Policies */}
          <section id="policy-changes" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">10. Changes to Credit Policies</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent reserves the right to modify credit allocations, consumption ratios, or expiration schedules as needed. Material updates will be published in revised platform documentation.
            </p>
          </section>

          {/* Section 11: Support Contacts & Key Principles */}
          <section id="need-help" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">11. Need Help & Contact Channels</h2>
                <p className="text-xs text-slate-400">Direct billing & credit support</p>
              </div>
            </div>

            <p className="text-slate-300 text-sm leading-relaxed">
              If you suspect a credit calculation error or require balance assistance, reach out to our team:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                <div className="text-slate-400 font-mono text-[10px] uppercase">Billing Enquiries</div>
                <div className="text-cyan-400 font-mono font-semibold">billing@nexgent.tech</div>
              </div>
              <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-1">
                <div className="text-slate-400 font-mono text-[10px] uppercase">General Technical Support</div>
                <div className="text-cyan-400 font-mono font-semibold">support@nexgent.tech</div>
              </div>
            </div>

            <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-slate-300 font-semibold">When contacting support, please include:</div>
              <ul className="text-slate-400 space-y-1 list-disc pl-4 font-mono text-[11px]">
                <li>Registered employer account email</li>
                <li>Transaction or invoice reference ID</li>
                <li>Date and timestamp of the activity</li>
                <li>Brief description of the expected vs actual credit behavior</li>
              </ul>
            </div>

            <div className="pt-2">
              <h3 className="text-xs font-mono text-cyan-400 font-semibold uppercase mb-3">Key Credit Governance Principles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                {keyPrinciples.map((principle, idx) => (
                  <div key={idx} className="p-3 bg-slate-950 border border-slate-800/80 rounded-xl flex items-start gap-2 text-slate-300">
                    <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                    <span className="text-[11px] leading-relaxed">{principle}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono mt-4">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent Billing Division. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default CreditUsagePolicy;